import React from 'react';
import { ShoppingBagIcon, TrashIcon, TagIcon, SparklesIcon, ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const OrderSummary = ({ 
  cartItems = [],
  bikeData = {},
  subtotal = 0, 
  safetyFee = 0, 
  doorstepCharge = 0,
  gst = 0,  
  total = 0, 
  handleRemoveItem,
  currentStep,
  isLoggedIn,
  step1Valid,
  handleContinueStep1,
  handleContinueStep2
}) => {
  const totalSavings = Array.isArray(cartItems) 
    ? cartItems.reduce((sum, item) => sum + ((item.price || 0) - (item.discountedPrice || 0)), 0)
    : 0;

  // Determine which button to show
  const getButtonConfig = () => {
    if (currentStep === 1) {
      return {
        show: true,
        text: 'Continue to Account',
        onClick: handleContinueStep1,
        disabled: !step1Valid || !isLoggedIn,
        disabledMessage: !isLoggedIn 
          ? 'Please login first' 
          : 'Complete all required fields'
      };
    } else if (currentStep === 2) {
      return {
        show: true,
        text: 'Continue to Bike Details',
        onClick: handleContinueStep2,
        disabled: false
      };
    }
    return { show: false };
  };

  const buttonConfig = getButtonConfig();

  return (
    <div className="lg:sticky lg:top-24 space-y-4">
      {/* Main Order Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5">
          <div className="flex items-center gap-2">
            <ShoppingBagIcon className="h-6 w-6 text-white" />
            <h2 className="text-lg font-bold text-white">Order Summary</h2>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Vehicle Details */}
          <div className="pb-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Your Vehicle</p>
            <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
              {bikeData.image && (
                <img 
                  src={bikeData.image} 
                  alt="Bike" 
                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0 shadow-sm"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="min-w-0">
                <p className="font-bold text-gray-900 text-base line-clamp-1">
                  {bikeData.manufacturer || 'Unknown'} {bikeData.model || 'Model'}
                </p>
                {bikeData.fuelType && (
                  <p className="text-sm text-gray-600 mt-0.5">{bikeData.fuelType}</p>
                )}
              </div>
            </div>
          </div>

          {/* Services List */}
          <div className="pb-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Selected Services ({cartItems.length})
            </p>
            {cartItems.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="group relative p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all border border-gray-100">
                    <div className="flex items-start gap-3">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded-lg flex-shrink-0 shadow-sm"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm mb-1.5 line-clamp-2 leading-snug">
                          {item.name || 'Service'}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-indigo-600 text-base">
                            ₹{item.discountedPrice || 0}
                          </p>
                          {item.price > item.discountedPrice && (
                            <p className="text-xs text-gray-400 line-through">
                              ₹{item.price}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem && handleRemoveItem(item.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                        title="Remove Service"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingBagIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No services added</p>
              </div>
            )}
          </div>

          {/* Price Breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between items-center text-gray-700">
              <span className="text-sm font-medium">Subtotal</span>
              <span className="text-base font-semibold">₹{subtotal.toFixed(2)}</span>
            </div>

            {/* <div className="flex justify-between items-center text-gray-700">
              <span className="flex items-center gap-2 text-sm font-medium">
                <TagIcon className="h-4 w-4" />
                Safety Fee (10%)
              </span>
              <span className="text-base font-semibold">₹{safetyFee.toFixed(2)}</span>
            </div> */}

 <div className="flex justify-between items-center text-gray-700">
              <span className="text-sm font-medium">GST(5%)</span>
              <span className="text-base font-semibold">₹{gst.toFixed(2)}</span>
            </div>
            {doorstepCharge > 0 && (
              <div className="flex justify-between items-center text-gray-700">
                <span className="text-sm font-medium">Doorstep Charge</span>
                <span className="text-base font-semibold">₹{doorstepCharge.toFixed(2)}</span>
              </div>
            )}

            <div className="pt-3 border-t-2 border-gray-300">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900">Total Amount</span>
                <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  ₹{total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Savings Badge */}
          {totalSavings > 0 && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <SparklesIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-bold">
                  You're saving ₹{totalSavings.toFixed(2)}!
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Continue Button Card - Step-by-Step */}
      {buttonConfig.show && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <button
            onClick={buttonConfig.onClick}
            disabled={buttonConfig.disabled}
            className={`
              w-full p-5 text-white font-bold text-base rounded-none transition-all duration-300 
              flex items-center justify-center gap-3 group
              ${buttonConfig.disabled
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              }
            `}
          >
            {buttonConfig.disabled ? (
              <>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>{buttonConfig.disabledMessage}</span>
              </>
            ) : (
              <>
                <span>{buttonConfig.text}</span>
                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
          <div className="px-5 py-3 bg-indigo-50 border-t border-indigo-100">
            <div className="flex items-center justify-center gap-4 text-xs text-indigo-700">
              <div className="flex items-center gap-1">
                <CheckCircleIcon className="h-4 w-4" />
                <span>Secure checkout</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircleIcon className="h-4 w-4" />
                <span>Free cancellation</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Need Assistance?</p>
        <div className="grid grid-cols-2 gap-3">
          <a 
            href="tel:+911234567890" 
            className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all text-sm font-semibold border border-indigo-100"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>Call Us</span>
          </a>
          <a 
            href="mailto:support@vegobike.com" 
            className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all text-sm font-semibold border border-indigo-100"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
